// push-order-20261021 / push-simple-20261021: armed order entry settings + Push.
//
// Push (simple): arming market-buys NOW at the panel's buy amount, records
// the tokens received and SOL spent, then sells pieces of THIS buy's tokens
// (piece = WalletHoldings' sell % of the tokens bought) into large outside
// buys: each piece fires on a single raw trade from another wallet, side
// buy, with SOL >= the piece's current SOL value. Stops once SOL back >= SOL
// spent; the rest of the tokens are kept (free).
//
// Trigger source: onRawTrade (useChartData), called synchronously from the
// chart's live trade push, so a piece fires on the trade itself.
import { useState, useRef, useEffect, useCallback } from 'react'
import { onRawTrade } from './useChartData'
import { getTokenBalance, getSOLBalance } from '../../utils/jupiter'

export const ORDER_ENTRY_KEY = 'solcheddar:orderEntry'
export const ORDER_ENTRY_DEFAULTS = {
  type: 'liq',              // 'liq' | 'push'  (liq = existing behavior)
  drawerOpen: false,
  liqSecs: 3,               // 1-10
  push: { maxSol: 1, target: 50 },   // maxSol: refuse to arm above; target: % up before first sell (push-target-20261022)
  chain: [],                // order-chain-20261021: ids armed in sequence (max 5)
}

const clamp = (v, lo, hi, d) => { const n = Number(v); return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : d }

export function normalizeOrderEntry(raw) {
  const D = ORDER_ENTRY_DEFAULTS, r = raw || {}, p = r.push || {}
  return {
    type: r.type === 'push' ? 'push' : (typeof r.type === 'string' && /^r:[a-z0-9]{3,16}$/.test(r.type)) ? r.type : 'liq',   // r:<id> = Grok recipe
    drawerOpen: !!r.drawerOpen,
    liqSecs: clamp(r.liqSecs, 1, 10, D.liqSecs),
    push: { maxSol: clamp(p.maxSol, 0.001, 1000, D.push.maxSol), target: clamp(p.target, 0, 1000, D.push.target) },
    chain: (Array.isArray(r.chain) ? r.chain : [])
      .filter((id, i, a) => typeof id === 'string' && (id === 'liq' || id === 'push' || /^r:[a-z0-9]{3,16}$/.test(id)) && a.indexOf(id) === i)
      .slice(0, 5),
  }
}

export function useOrderEntry() {
  const [cfg, setCfg] = useState(() => {
    try { return normalizeOrderEntry(JSON.parse(localStorage.getItem(ORDER_ENTRY_KEY) || 'null')) }
    catch { return normalizeOrderEntry(null) }
  })
  useEffect(() => { try { localStorage.setItem(ORDER_ENTRY_KEY, JSON.stringify(cfg)) } catch {} }, [cfg])
  const update = useCallback((patch) => setCfg(c => normalizeOrderEntry({
    ...c, ...patch, push: { ...c.push, ...(patch.push || {}) },
  })), [])
  return [cfg, update]
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms))
const BUY_FILL_WAIT_MS = 15000     // balance must rise within this after the buy call
const SELL_SETTLE_WAIT_MS = 8000   // background: wait this long for the SOL balance to show a sell
const POLL_MS = 700
const BE_BUF = 0.02                 // push-target-20261022: fees + slippage cushion on breakeven

// Poll fn() until pred(value) or timeout; returns the last value read.
async function pollUntil(fn, pred, timeoutMs) {
  const t0 = Date.now(); let v = null
  while (Date.now() - t0 < timeoutMs) {
    try { v = await fn() } catch { v = null }
    if (v != null && pred(v)) return v
    await sleep(POLL_MS)
  }
  return v
}

// status: null | { phase: 'buying'|'selling'|'done', text, token }
export function usePushOrder({ cfg, trading, currentToken, publicKey, buyAmountSol, sellPercentage, isFutures, showToast, onEnd }) {
  const [status, setStatus] = useState(null)
  const runRef = useRef(null)
  const ctxRef = useRef({})
  ctxRef.current = { cfg, trading, currentToken, publicKey, buyAmountSol, sellPercentage, isFutures, showToast, onEnd }

  const show = (r, phase, text) => { r.phase = phase; setStatus({ phase, text, token: r.token }) }

  // order-chain-20261021: ok=true only for the done (breakeven / all sold) stops.
  const stop = useCallback((why, ok) => {
    const r = runRef.current
    if (!r) return
    runRef.current = null
    r.dead = true
    try { r.off?.() } catch {}
    setStatus(why ? { phase: 'done', text: why, token: r.token } : null)
    try { ctxRef.current.onEnd?.(!!ok) } catch {}
  }, [])

  // SOL back = reconciled (wallet SOL balance since the buy, read in the
  // background) + price estimates for pieces the balance hasn't shown yet.
  const backOf = (r) => {
    let b = r.recBack
    for (let i = r.recN; i < r.est.length; i++) b += r.est[i]
    return b
  }
  const backText = (r) => `${backOf(r).toFixed(3)}/${r.spent.toFixed(3)} SOL back`
  const checkDone = (r) => {
    if (r.dead) return true
    if (backOf(r) >= r.spent * 0.999) {
      ctxRef.current.showToast?.(`Push done: ~${backOf(r).toFixed(3)} SOL back - rest is free`, 'buy')
      stop(`done (free tokens) - ${backText(r)}`, true)
      return true
    }
    if (r.soldTok >= r.tokens * 0.999 || r.bal <= 0) { stop(`done - all pushed tokens sold, ${backText(r)}`, true); return true }
    return false
  }

  // Background: wait for the SOL balance to show sells up to piece k, then
  // replace their estimates with the real total. Never blocks the next piece.
  const reconcile = async (r, k) => {
    try {
      if (r.baseSol == null) return
      const want = r.baseSol + r.recBack + 1e-6
      const v = await pollUntil(() => getSOLBalance(r.pk), (x) => x > want, SELL_SETTLE_WAIT_MS)
      if (r.dead || v == null || !(v > want) || k < r.recN) return
      r.recBack = v - r.baseSol
      r.recN = k
      if (!r.firing) { if (!checkDone(r)) show(r, 'selling', `selling ${r.n} - ${backText(r)}`) }
    } catch {}
  }

  // Size the next piece at `price`; null if nothing left to sell.
  const sizePiece = (r, price) => {
    const pct = Math.max(1, Math.min(100, Number(ctxRef.current.sellPercentage) || 10))
    let tok = Math.min(r.tokens * pct / 100, r.tokens - r.soldTok, r.bal)   // never past this buy's tokens
    if (!(tok > 0) || !(price > 0)) return null
    const need = r.spent - backOf(r)
    if (!(need > 0)) return null
    // push-target-20261022: never sell under breakeven; first sell waits for the target.
    const entry = r.tokens > 0 ? r.spent / r.tokens : 0                      // SOL per token incl. fees
    if (!(entry > 0)) return null
    const floor = entry * (1 + BE_BUF)
    if (!r.hitTarget) {
      const tgt = Math.max(0, Number(ctxRef.current.cfg?.push?.target) || 0)
      if (price < Math.max(floor, entry * (1 + tgt / 100))) return null
      r.hitTarget = true
    }
    if (price < floor) return null
    const net = price * (1 - BE_BUF)                                         // what a token likely fetches after slippage/fees
    if (tok * net > need) tok = need / net                                   // last piece: just to breakeven
    return { tok, sol: tok * price }
  }

  // One sell send. r.firing is set synchronously by the caller and cleared
  // when this send resolves; buys arriving meanwhile wait in r.pending.
  const firePiece = async (r, pieceTok, pieceSol) => {
    const { trading, showToast } = ctxRef.current
    let sent = false
    try {
      const frac = Math.max(0.0005, Math.min(1, pieceTok / r.bal))
      show(r, 'selling', `selling ${r.n + 1} - ${backText(r)}`)
      await trading.executeMarketOrder('sell', { sellFraction: frac, token: r.token })
      sent = true
      window.dispatchEvent(new CustomEvent('solcheddar-sell-executed', { detail: { token: r.token } }))
      r.n++
      r.soldTok += pieceTok
      r.bal = Math.max(0, r.bal - pieceTok)
      r.est.push(pieceSol)
      reconcile(r, r.n)
    } catch (e) {
      showToast?.(`Push sell failed: ${e?.message || e}`, 'sell')
    } finally {
      r.firing = false
    }
    if (checkDone(r)) return
    show(r, 'selling', `selling ${r.n} - ${backText(r)}${sent ? '' : ' (last failed)'}`)
    // Queued big buys: fire the next piece right away.
    while (r.pending.length && !r.dead && !r.firing) {
      const q = r.pending.shift()
      const p = sizePiece(r, q.price)
      if (!p) { checkDone(r); return }
      if (q.sol < p.sol) continue
      r.firing = true
      firePiece(r, p.tok, p.sol)
      return
    }
  }

  const arm = useCallback(async (opts) => {   // opts.sol: recipe buy size override
    const { cfg, currentToken, publicKey, buyAmountSol, isFutures, showToast, trading } = ctxRef.current
    try {
      if (runRef.current) {
        const r = runRef.current
        stop(r.phase === 'selling' ? `stopped - ${backText(r)}, rest held` : 'cancelled')
        showToast?.('Push cancelled', 'sell')
        return
      }
      if (isFutures) return showToast?.('Push is Solana only', 'sell')
      if (!currentToken) return showToast?.('Select a token first', 'sell')
      if (!publicKey) return showToast?.('Connect wallet first', 'sell')
      const sol = Number(opts?.sol ?? buyAmountSol) || 0
      if (!(sol > 0)) return showToast?.('Set a buy size first', 'sell')
      if (sol > cfg.push.maxSol) return showToast?.(`Push refused: ${sol} SOL > max ${cfg.push.maxSol} SOL`, 'sell')

      const pk = publicKey
      const me = publicKey.toString()
      const r = { token: currentToken, pk, me, sol, spent: sol, tokens: 0, bal: 0, solBal: null,
        soldTok: 0, n: 0, firing: false, pending: [], est: [], recBack: 0, recN: 0, baseSol: null, dead: false, phase: 'buying', seen: [] }
      runRef.current = r
      show(r, 'buying', `buying ${sol} SOL...`)

      const pre = await getTokenBalance(pk, r.token)
      const preSol = await getSOLBalance(pk).catch(() => null)
      if (pre == null) { stop('stopped - could not read token balance'); return }
      if (r.dead) return
      try {
        await trading.executeMarketOrder('buy', { buyAmountSol: sol, token: r.token })
      } catch (e) {
        return stop('buy failed - nothing sold')
      }
      if (r.dead) return
      const post = await pollUntil(() => getTokenBalance(pk, r.token), (v) => v > pre, BUY_FILL_WAIT_MS)
      if (r.dead) return
      if (!(post != null && post > pre)) return stop('buy not filled - nothing sold')
      r.tokens = post - pre
      r.bal = post
      const postSol = await getSOLBalance(pk).catch(() => null)
      if (preSol != null && postSol != null) {
        const d = preSol - postSol           // includes fees/rent: true cost to get back
        if (d > sol * 0.5 && d < sol * 1.5) r.spent = d
      }
      r.baseSol = postSol
      if (r.dead) return

      r.off = onRawTrade((tok, wallet, side, tsol, price, t) => {
        try {
          if (r.dead || r.phase !== 'selling') return
          if (tok !== r.token || side !== 'buy' || !wallet || wallet === r.me) return
          if (!(price > 0) || !(tsol > 0)) return
          // One piece per triggering buy (two chart slots replay the same trade).
          const key = `${wallet}|${t}|${tsol}`
          if (r.seen.includes(key)) return
          if (r.firing) {
            // A piece is in flight: queue this buy (cap 3), fire after it resolves.
            const q = sizePiece(r, price)
            if (q && tsol >= q.sol && r.pending.length < 3) { r.seen.push(key); if (r.seen.length > 20) r.seen.shift(); r.pending.push({ price, sol: tsol }) }
            return
          }
          const p = sizePiece(r, price)
          if (!p || tsol < p.sol) return
          r.seen.push(key); if (r.seen.length > 20) r.seen.shift()
          r.firing = true                       // sync guard BEFORE any await
          firePiece(r, p.tok, p.sol)
        } catch {}
      })
      show(r, 'selling', `selling 0 - ${backText(r)}`)
      showToast?.(`Push: got ${r.tokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} tokens for ${r.spent.toFixed(3)} SOL - selling into big buys`, 'buy')
    } catch (e) {
      try { console.warn('[Push] arm', e) } catch {}
      stop('stopped - error')
    }
  }, [stop]) // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = useCallback(() => {
    const r = runRef.current
    if (!r) { setStatus(null); return }
    stop(r.phase === 'selling' ? `stopped - ${backText(r)}, rest held` : 'cancelled')
  }, [stop])

  // Token switch: stop (never sell another coin's tokens from here).
  useEffect(() => {
    const r = runRef.current
    if (r && currentToken && currentToken !== r.token) stop(r.phase === 'selling' ? `stopped - token changed, ${backText(r)}` : 'cancelled - token changed')
  }, [currentToken, stop])

  useEffect(() => () => { try { stop() } catch {} }, [stop])

  return { status, armed: !!runRef.current && !!status && status.phase !== 'done', running: () => !!runRef.current, arm, cancel, clear: () => setStatus(null) }
}
